import React, { useState, useMemo } from 'react';
import { RoomType } from '../../../../services/ConfigurationService/types/roomType';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Page, Sort, RowSelectEventArgs } from '@syncfusion/ej2-react-grids';
import EditRoomDialog from '../dialogs/EditRoomDialog';
import { Box, Typography, TextField, Stack, IconButton, FormControlLabel, Switch } from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';

interface RoomGridRow {
  id: string;
  code: string;
  description: string;
  status: string;
}

export function RoomGrid({ rooms, centerId, onRoomSaved }: {
  rooms: RoomType[];
  centerId: string;
  onRoomSaved: () => void;
}) {
  const [editRoom, setEditRoom] = React.useState<RoomType | null>(null);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Filter rooms based on search term and inactive status
  const filteredRooms = useMemo(() => {
    let filtered = rooms;
    
    // Filter by inactive status
    if (!showInactive) {
      filtered = filtered.filter(room => !room.isInactive);
    }
    
    // Filter by search term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(room => 
        (room.code?.toLowerCase().includes(searchLower)) ||
        (room.description?.toLowerCase().includes(searchLower))
      );
    }
    
    return filtered;
  }, [rooms, searchTerm, showInactive]);

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  // Transform rooms to include status field for grid display
  const roomsWithStatus = useMemo(() => 
    filteredRooms.map(room => ({
      ...room,
      status: room.isInactive ? 'Inactive' : 'Active'
    })), [filteredRooms]);

  const handleRowSelection = (args: RowSelectEventArgs) => {
    if (args.data) {
      const selectedRow = args.data as RoomGridRow;
      const room = rooms.find(r => r.id === selectedRow.id);
      if (room) {
        setEditRoom(room);
        setEditDialogOpen(true);
      }
    }
  };

  const handleDialogSave = () => {
    setEditDialogOpen(false);
    setEditRoom(null);
    onRoomSaved();
  };

  if (rooms.length === 0) {
    return (
      <Box>
        {/* Search Row */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SearchIcon sx={{ color: '#64748b' }} />
            <TextField
              fullWidth
              size="small"
              placeholder="Search by code or description..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#3b82f6',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3b82f6',
                  }
                }
              }}
            />
            {searchTerm && (
              <IconButton
                size="small"
                onClick={handleClearSearch}
                sx={{ color: '#64748b' }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  size="small"
                />
              }
              label="Show Inactive"
              sx={{ ml: 2, whiteSpace: 'nowrap' }}
            />
          </Stack>
        </Box>
        
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
            No rooms found
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            No rooms available for this center
          </Typography>
        </Box>
      </Box>
    );
  }

  // Handle no search results
  if (filteredRooms.length === 0 && searchTerm.trim()) {
    return (
      <Box>
        {/* Search Row */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SearchIcon sx={{ color: '#64748b' }} />
            <TextField
              fullWidth
              size="small"
              placeholder="Search by code or description..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#3b82f6',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3b82f6',
                  }
                }
              }}
            />
            {searchTerm && (
              <IconButton
                size="small"
                onClick={handleClearSearch}
                sx={{ color: '#64748b' }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  size="small"
                />
              }
              label="Show Inactive"
              sx={{ ml: 2, whiteSpace: 'nowrap' }}
            />
          </Stack>
        </Box>
        
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
            No rooms match your search
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            Try adjusting your search term: "{searchTerm}"
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Search Row */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <SearchIcon sx={{ color: '#64748b' }} />
          <TextField
            fullWidth
            size="small"
            placeholder="Search by code or description..."
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#ffffff',
                '& fieldset': {
                  borderColor: '#e2e8f0',
                },
                '&:hover fieldset': {
                  borderColor: '#3b82f6',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#3b82f6',
                }
              }
            }}
          />
          {searchTerm && (
            <IconButton
              size="small"
              onClick={handleClearSearch}
              sx={{ color: '#64748b' }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                size="small"
              />
            }
            label="Show Inactive"
            sx={{ ml: 2, whiteSpace: 'nowrap' }}
          />
        </Stack>
      </Box>

      {/* Grid */}
      <GridComponent
        dataSource={roomsWithStatus}
        height={350}
        allowPaging={true}
        allowSorting={true}
        pageSettings={{ pageSize: 25 }}
        selectionSettings={{ mode: 'Row', type: 'Single' }}
        rowSelected={handleRowSelection}
        style={{
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          backgroundColor: '#ffffff'
        }}
        enableHover={true}
      >
        <ColumnsDirective>
          <ColumnDirective 
            field="code" 
            headerText="Code" 
            width="120"
            textAlign="Left"
          />
          <ColumnDirective 
            field="description" 
            headerText="Description" 
            width="200"
            textAlign="Left"
          />
          <ColumnDirective 
            field="status" 
            headerText="Status" 
            width="100"
            textAlign="Left"
          />
        </ColumnsDirective>
        <Inject services={[Page, Sort]} />
      </GridComponent>
      
      {editDialogOpen && editRoom && (
        <EditRoomDialog
          open={editDialogOpen}
          onClose={() => { setEditDialogOpen(false); setEditRoom(null); }}
          centerId={centerId}
          room={editRoom}
          onSave={handleDialogSave}
        />
      )}
    </Box>
  );
}


